import React, {ReactNode, RefObject} from "react";
import i18next from "i18next";
import {I18nGlobals} from "../../shared/translation/i18nInterface";
import {Button} from "primereact/button";
import Resizeable, {ResizeableDirection} from "../../shared/components/Resizeable";
import PortalContext from "../PortalContext";
import {News as PortalNews, NewsPanelEntry} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import Translate from "../../shared/translation/Translate";
import FunctionHelper from "../../shared/helper/FunctionHelper";
import Timer from "../../shared/class/Timer";
import PortalPureComponent from "../../shared/components/PortalPureComponent";

const LANG_PORTAL: RegExp = /{languagePortal}/g;
const LANG_BROWSER: RegExp = /{languageBrowser}/g;

interface NewsProperties extends I18nGlobals {
	context: PortalContext;
	newsActive: boolean;
	startScreen: RefObject<HTMLDivElement>;
	toggleNews: () => void;
}

interface NewsState {
	timestamp: string;
	newsWidth: string;
}

export class NewsDefinition extends PortalPureComponent<NewsProperties, NewsState> {
	private readonly blankPage: string = "about:blank";
	private refreshTimer: Timer;

	constructor(props: Readonly<NewsProperties>) {
		super(props);

		let timestamp: string = new Date().getTime().toString();
		this.state = {
			newsWidth: "25%",
			timestamp: timestamp
		}

		this.refreshTimer = new Timer(() => {
			let timestamp: string = new Date().getTime().toString();

			this.setState({
				timestamp: timestamp
			});
		}, PortalNews.getContentRefreshIntervalDefault() * 1000, true);
	}

	private applyParams(url: string): string {
		let browserLocale: string = navigator.languages && navigator.languages.length > 0 ?
			navigator.languages[0] : navigator.language;

		let result: string = url;
		result = result.replace(LANG_PORTAL, i18next.language.substr(0, 2));
		result = result.replace(LANG_BROWSER, browserLocale.substr(0, 2));
		return result;
	}

	private buildUrl(): string {
		let newsSettings: PortalNews | undefined = this.props.context.info.newsPanel;

		if (!FunctionHelper.exists(newsSettings) || newsSettings!.entry!.length === 0) {
			return this.blankPage;
		}

		let url: string = FunctionHelper.applyValue(newsSettings!.entry![0].url, NewsPanelEntry.getUrlDefault());
		if (url === "") {
			return this.blankPage;
		}

		url = this.applyParams(url);

		let params: URLSearchParams = new URLSearchParams();
		let urlParts: Array<string> = url.split("?");
		if (FunctionHelper.exists(urlParts[1])) {
			params = new URLSearchParams(urlParts[1]);
		}

		let timestamp: string = new Date().getTime().toString();
		params.set("webpdf_timestamp", timestamp);

		return urlParts[0] + "?" + params;
	}

	/**
	 * This is called when the component is mounted
	 */
	public async componentDidMount(): Promise<void> {
		let newsSettings: PortalNews | undefined = this.props.context.info.newsPanel;

		if (FunctionHelper.exists(newsSettings)) {
			this.refreshTimer.setTime(newsSettings!.contentRefreshInterval! * 1000);
		}

		this.refreshTimer.reset();
	}

	/**
	 * This is called when the components props or state is updated
	 */
	public async componentDidUpdate(
		prevProps: Readonly<NewsProperties>, prevState: Readonly<NewsState>, snapshot?: any
	): Promise<void> {
		if (prevProps.language !== this.props.language) {
			await this.componentDidMount();
		}
	}

	/**
	 * This is called when the component is unmounted
	 */
	public async componentWillUnmount(): Promise<void> {
		await super.componentWillUnmount();

		this.refreshTimer.pause();
	}

	/**
	 * render component as react node
	 */
	public render(): ReactNode {
		let classlist: Array<string> = ["left", "sidebar", "news"];

		if (this.props.newsActive) {
			classlist.push("in");
		}

		return (
			<>
				<div className={"news mobile"}>
					<div className="body">
						<h2>
							<Translate {...this.props} data="startscreen.panels.news.title" namespace="screen"/>
						</h2>
						<iframe title="news" scrolling="yes" src={this.buildUrl()}/>
					</div>
				</div>
				<div className={classlist.join(" ")} style={{width: this.state.newsWidth}}>
					<Button className="toggle-btn" icon="icon icon-angle-left"
							onClick={() => this.props.toggleNews()}/>
					<Resizeable direction={ResizeableDirection.HORIZONTAL} container={this.props.startScreen}
								invert={false}
								resizeCallback={(options: { width: string }) => this.setState({
									newsWidth: options.width
								})}/>
					<div className="body">
						<h2>
							<Translate {...this.props} data="startscreen.panels.news.title" namespace="screen"/>
						</h2>
						<iframe title="news" src={this.buildUrl()}/>
					</div>
				</div>
			</>
		);
	}
}

const News = React.memo(NewsDefinition,
	(prevProps: Readonly<NewsProperties>, nextProps: Readonly<NewsProperties>) => {
		if (nextProps.language !== prevProps.language) {
			return false;
		}

		if (nextProps.newsActive !== prevProps.newsActive) {
			return false;
		}

		return true;
	});
export default News;